import nl2br from "hoxton-shared/utils/nl2br";

import type { Err } from "@/types/types";

type Config = {
    content?: string;
    closable?: boolean;
    duration?: number;
};

type ViewUINotification = {
    config: (config: Config) => void;
    error: (config: Config) => void;
    info: (config: Config) => void;
    success: (config: Config) => void;
    warning: (config: Config) => void;
    loading: (config: Config) => void;
};

type NotificationUpdate = (message: string, description?: string, config?: Config) => void;
type NotificationType = "error" | "info" | "success" | "warning" | "loading";

type Snackbar = {
    defaultMessage: string;
    notification: ViewUINotification | null;
    set: (notification: ViewUINotification) => void;
    notify: (type: NotificationType, message: string, description?: string, config?: Config) => void;
    error: NotificationUpdate;
    info: NotificationUpdate;
    success: NotificationUpdate;
    warning: NotificationUpdate;
    render: (config: Config) => void;
    fieldRequired: (message: string) => void;
    unauthorized: (err: Err) => void;
    authentication: (err: Err) => void;
    image: (err: Err) => void;
    graphQLErrors: (errors: Err | Err[]) => void;
    graphQLError: (err: Err) => void;
};

const snackbar: Snackbar = {
    defaultMessage: "Unable to display the error here. Please check your console for error details",
    notification: null,

    set(notification) {
        if (!notification) {
            return;
        }

        this.notification = notification;

        this.notification.config({ duration: 4 });
    },

    notify(type, message, description = "", config = {}) {
        const content = nl2br(description ? `${message}: ${description}` : message);

        if (!this.notification) {
            console.error("Notification not set");

            return;
        }

        this.notification[type]({ content, ...config });
    },

    error(message, description, config = { closable: true }) {
        this.notify("error", message, description, config);
    },

    info(message, description, config = { closable: true }) {
        this.notify("info", message, description, config);
    },

    success(message, description, config = { closable: true }) {
        this.notify("success", message, description, config);
    },

    warning(message, description, config = { closable: true }) {
        this.notify("warning", message, description, config);
    },

    render(config = { closable: true }) {
        this.notification?.info(config);
    },

    fieldRequired(message) {
        this.error("Field Required", message);
    },

    unauthorized(err) {
        this.error("Unauthorized", err.message || this.defaultMessage);
    },

    authentication(err) {
        const errorMessage = typeof err === "string" ? err : err.message || this.defaultMessage;

        this.error("Authentication Error", errorMessage);
    },

    image(err) {
        this.error("Image Error", err.message || this.defaultMessage);
    },

    graphQLErrors(errors) {
        const errorArray = Array.isArray(errors) ? errors : [errors];

        errorArray.forEach(err => {
            if (err.message === "Unauthorized") {
                this.error("Unauthorized");
            } else {
                this.error("GraphQL Error", err.message || this.defaultMessage);
            }
        });
    },

    graphQLError(err) {
        if (err.statusCode && [401, 403].includes(err.statusCode)) {
            this.unauthorized(err);
        } else if (Array.isArray(err.graphQLErrors)) {
            this.graphQLErrors(err.graphQLErrors);
        }
    }
};

export default snackbar;
